import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Stack,
  CircularProgress,
  Chip,
  Tabs,
  Tab,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PolicyIcon from '@mui/icons-material/Policy';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import CancelIcon from '@mui/icons-material/Cancel';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import PetsIcon from '@mui/icons-material/Pets';
import SmokingRoomsIcon from '@mui/icons-material/SmokingRooms';
import PaymentIcon from '@mui/icons-material/Payment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getHotelInfo, getHotelPolicies } from './nexastayApi';
import { useNexaStayTheme } from './NexaStayThemeContext';

const POLICY_ICON_MAP = {
  terms_and_conditions: PolicyIcon,
  privacy_policy: PrivacyTipIcon,
  child_policy: ChildCareIcon,
  cancellation_policy: CancelIcon,
  refund_policy: CancelIcon,
  check_in_policy: LoginIcon,
  check_out_policy: LogoutIcon,
  pet_policy: PetsIcon,
  smoking_policy: SmokingRoomsIcon,
  payment_policy: PaymentIcon,
  age_restriction: InfoOutlinedIcon,
  age_restriction_policy: InfoOutlinedIcon,
  other: MenuBookIcon,
};

function getPolicyIcon(policyType) {
  return POLICY_ICON_MAP[policyType] || PolicyIcon;
}

export default function TermsPolicyDialog({ open, onClose, hotelId, selectedPolicyType }) {
  const theme = useNexaStayTheme();
  const isDark = theme?.mode === 'dark';
  const accent = theme?.accent || '#4ce1b6';

  const [hotel, setHotel] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    if (!open || !hotelId) {
      setPolicies([]);
      setHotel(null);
      setError(null);
      setTabIndex(0);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [infoRes, policiesRes] = await Promise.all([
          getHotelInfo(hotelId),
          getHotelPolicies(hotelId),
        ]);
        if (!cancelled) {
          setHotel(infoRes);
          setPolicies(policiesRes.policies || []);
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load terms and policies.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, hotelId]);

  // When policies load (or selectedPolicyType changes), select the matching tab
  useEffect(() => {
    if (!policies.length) return;
    if (selectedPolicyType) {
      const idx = policies.findIndex((p) => p.policy_type === selectedPolicyType);
      if (idx >= 0) setTabIndex(idx);
      else setTabIndex(0);
    } else {
      setTabIndex(0);
    }
  }, [policies, selectedPolicyType]);

  const hotelName = hotel?.name || 'Hotel';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '88vh',
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: isDark ? alpha(theme?.paper ?? '#1e1e1e', 0.92) : alpha(theme?.paper ?? '#fff', 0.94),
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: `1px solid ${alpha(theme?.borderAccent ?? '#333', 0.2)}`,
          boxShadow: isDark ? '0 24px 80px rgba(0,0,0,0.5)' : '0 24px 80px rgba(0,0,0,0.12)',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            bgcolor: 'rgba(0,0,0,0.35)',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          py: 2,
          px: 3,
          borderBottom: `1px solid ${theme?.borderAccent}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(accent, 0.2),
              color: accent,
            }}
          >
            <MenuBookIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: theme?.textPrimary, fontWeight: 700 }}>
              Terms & Policies
            </Typography>
            <Typography variant="body2" sx={{ color: theme?.textSecondary, fontWeight: 500 }}>
              {loading ? 'Loading…' : hotelName}
            </Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: theme?.textSecondary,
            '&:hover': { color: theme?.textPrimary, bgcolor: alpha(accent, 0.1) },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={40} sx={{ color: accent }} />
          </Box>
        ) : error ? (
          <Box sx={{ px: 3, py: 4 }}>
            <Typography variant="body2" sx={{ color: theme?.textSecondary }}>{error}</Typography>
          </Box>
        ) : policies.length === 0 ? (
          <Box sx={{ px: 3, py: 6, textAlign: 'center' }}>
            <PolicyIcon sx={{ fontSize: 48, color: theme?.textMuted, opacity: 0.5, mb: 2 }} />
            <Typography variant="body2" sx={{ color: theme?.textSecondary }}>
              No terms or policies have been published for this property yet.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <Tabs
              value={tabIndex}
              onChange={(_, v) => setTabIndex(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                flexShrink: 0,
                minHeight: 48,
                borderBottom: `1px solid ${theme?.borderAccent}`,
                px: 1,
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48 },
                '& .Mui-selected': { color: accent },
                '& .MuiTabs-indicator': { bgcolor: accent },
                '& .MuiTabs-scrollButtons': { color: theme?.textSecondary },
              }}
            >
              {policies.map((policy, index) => {
                const IconComponent = getPolicyIcon(policy.policy_type);
                return (
                  <Tab
                    key={policy.policy_type || index}
                    icon={<IconComponent sx={{ fontSize: 18, mb: 0 }} />}
                    iconPosition="start"
                    label={policy.policy_type_display || policy.title}
                  />
                );
              })}
            </Tabs>
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                px: 3,
                py: 2,
                '&::-webkit-scrollbar': { width: 8 },
                '&::-webkit-scrollbar-track': { bgcolor: alpha(theme?.borderAccent, 0.1), borderRadius: 4 },
                '&::-webkit-scrollbar-thumb': { bgcolor: alpha(accent, 0.35), borderRadius: 4 },
                '&::-webkit-scrollbar-thumb:hover': { bgcolor: alpha(accent, 0.5) },
              }}
            >
              {policies[tabIndex] && (() => {
                const policy = policies[tabIndex];
                const IconComponent = getPolicyIcon(policy.policy_type);
                return (
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: isDark ? alpha(accent, 0.06) : alpha(accent, 0.04),
                      border: `1px solid ${alpha(accent, 0.15)}`,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(accent, 0.15),
                          color: accent,
                        }}
                      >
                        <IconComponent sx={{ fontSize: 18 }} />
                      </Box>
                      <Typography variant="subtitle2" sx={{ color: theme?.textPrimary, fontWeight: 700 }}>
                        {policy.title}
                      </Typography>
                      {policy.policy_type_display && (
                        <Chip
                          label={policy.policy_type_display}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            bgcolor: alpha(accent, 0.12),
                            color: accent,
                          }}
                        />
                      )}
                      {policy.version && (
                        <Chip
                          label={`v${policy.version}`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.65rem', borderColor: theme?.borderAccent, color: theme?.textSecondary }}
                        />
                      )}
                    </Stack>
                    {policy.summary && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          color: theme?.textSecondary,
                          fontStyle: 'italic',
                          mb: 1.5,
                          pl: 1.5,
                          borderLeft: `2px solid ${alpha(accent, 0.4)}`,
                        }}
                      >
                        {policy.summary}
                      </Typography>
                    )}
                    {policy.content ? (
                      <Box
                        component="div"
                        sx={{
                          color: theme?.textPrimary,
                          fontSize: '0.875rem',
                          lineHeight: 1.65,
                          '& p': { mb: 1 },
                          '& ul, & ol': { pl: 2, mb: 1 },
                          '& a': { color: accent },
                          '& strong': { fontWeight: 700 },
                        }}
                        dangerouslySetInnerHTML={{ __html: policy.content }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ color: theme?.textSecondary }}>No content for this section.</Typography>
                    )}
                  </Box>
                );
              })()}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
